import { useCallback } from "react";

import {
  getCMEAccessoriesToDisplay,
  getSelectedAccessories,
} from "../../helpers/cmeHelper";
import { getCMSAccessories } from "../../helpers/cmsHelper";
import { hasValidSubGroupItems } from "../../helpers/hasValidSubGroupItems";
import { GaEvent } from "../fe-modules/GA/static";
import useGA from "../fe-modules/GA/useGA";
import type { iConfiguratorAccessory } from "./types";
import type { iItem } from "../../uiComponents/molecules/uimDropdown/UimDropdown";
import { useDataContext } from "./DataProvider";
import { useCmeContext } from "../fe-cme/CMEProvider";

const useConfiguratorData = () => {
  const { cmeSiloUiData, configuratorState } = useDataContext();
  const { cmeOptions, selectOption } = useCmeContext();
  const { triggerGAEvent } = useGA();

  const getCmeOption = useCallback(
    (id: string) => {
      const accessories = getCMEAccessoriesToDisplay(cmeOptions);
      return accessories?.options?.find((option: any) => option.name === id);
    },
    [cmeOptions],
  );

  const getAccessorySubGroupItems = useCallback(
    (accessory: iConfiguratorAccessory): iItem[] => {
      if (!hasValidSubGroupItems(accessory)) {
        return [];
      }

      return accessory.uiSubGroup?.options.filter(
        (item: iItem) => !!getCmeOption(item.value),
      );
    },
    [getCmeOption],
  );

  const buildAccessoryItem = useCallback(
    (accessoryKey: string, cmsAccessory: any) => {
      const acc = getCmeOption(accessoryKey);
      return {
        id: accessoryKey,
        selected: acc?.selected,
        image: {
          alt: cmsAccessory.title,
          mobileSrc: cmsAccessory.thumbnail,
          src: cmsAccessory.thumbnail,
        },
        tLabel: cmsAccessory.title,
        onClick: () => {
          triggerGAEvent(GaEvent.CONFIGURATOR_SELECT_ACCESSORY, {
            event: GaEvent.CONFIGURATOR_SELECT_ACCESSORY,
            accessoryCategoryId: cmsAccessory.uiGroup,
            accessoryName: cmsAccessory.title,
          });
          selectOption(accessoryKey);
        },
        description: cmsAccessory.description,
        type: cmsAccessory.bullBarOptions?.type,
        colour: cmsAccessory.bullBarOptions?.colour,
        configuration: cmsAccessory.bullBarOptions?.configuration,
      };
    },
    [getCmeOption, selectOption, triggerGAEvent],
  );

  const buildAccessoryGroup = useCallback(
    (
      selectedAccessories: string[],
      accessoryGroup: any,
      items: iItem[],
      defaultSubGroupAccessoryKey: string,
    ) => ({
      id: defaultSubGroupAccessoryKey,
      onClick: () => {
        triggerGAEvent(GaEvent.CONFIGURATOR_SELECT_ACCESSORY, {
          event: GaEvent.CONFIGURATOR_SELECT_ACCESSORY,
          accessoryCategoryId: accessoryGroup.uiGroup,
          accessoryName: accessoryGroup.title,
        });
        selectOption(defaultSubGroupAccessoryKey);
      },
      selected: selectedAccessories.includes(defaultSubGroupAccessoryKey),
      defaultValue: defaultSubGroupAccessoryKey,
      image: {
        alt: accessoryGroup.title,
        mobileSrc: accessoryGroup.thumbnail,
        src: accessoryGroup.thumbnail,
      },
      tLabel: accessoryGroup.title,
      options: {
        tLabel: accessoryGroup.uiSubGroup.tLabel,
        items,
      },
    }),
    [selectOption, triggerGAEvent],
  );

  const handleSubGroup = useCallback(
    (accessory: any, selectedAccessories: any, subGroupItems: iItem[], items: any[]) => {
      const defaultSubGroupAccessoryKey: string = subGroupItems[0]?.value;
      if (subGroupItems.length > 0) {
        items.push(
          buildAccessoryGroup(
            selectedAccessories,
            accessory,
            subGroupItems,
            defaultSubGroupAccessoryKey,
          ),
        );
      }
    },
    [buildAccessoryGroup],
  );

  const handleNonSubGroup = useCallback(
    (accessoryKey: string, cmsAcc: any, items: any[]) => {
      if (getCmeOption(accessoryKey)) {
        items.push(buildAccessoryItem(accessoryKey, cmsAcc));
      }
    },
    [getCmeOption, buildAccessoryItem],
  );

  const getConfiguratorAccessories = useCallback(() => {
    if (!cmeSiloUiData) {
      return [];
    }

    const selectedAccessories = getSelectedAccessories(cmeOptions);
    const cmsAccessories = getCMSAccessories(cmeSiloUiData);
    const accessoryKeys = Object.keys(cmsAccessories);

    const items: any[] = [];

    for (const accessoryKey of accessoryKeys) {
      const cmsAcc = cmsAccessories[accessoryKey];
      const isSubGroup = cmsAcc.uiSubGroup?.options?.length > 0;
      const subGroupItems = getAccessorySubGroupItems(cmsAcc);

      if (cmsAcc.uiGroup === configuratorState?.accessoryCategoryId.replace("_", " ")) {
        if (isSubGroup) {
          handleSubGroup(cmsAcc, selectedAccessories, subGroupItems, items);
        } else {
          handleNonSubGroup(accessoryKey, cmsAcc, items);
        }
      }
    }

    return items;
  }, [
    cmeSiloUiData,
    cmeOptions,
    getAccessorySubGroupItems,
    configuratorState?.accessoryCategoryId,
    handleSubGroup,
    handleNonSubGroup,
  ]);

  return {
    getCmeOption,
    getAccessorySubGroupItems,
    getConfiguratorAccessories,
  };
};

export default useConfiguratorData;
